import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Plus, Filter } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { PostureChip } from '@/components/ui/posture-chip';
import {
  useDeviationsListQuery,
  DEVIATION_SEVERITIES,
  DEVIATION_STATUSES,
  type DeviationSeverity,
  type DeviationStatus,
  type DeviationSummary,
} from '@/lib/api/deviations';
import { DeviationCard } from '@/components/contract/deviation-card';
import {
  CreateDeviationModal,
  TransitionDeviationModal,
} from '@/components/modals/deviation-modals';
import { EscalationBanner } from '@/components/contract/escalation-banner';
import { ContractHeader } from '@/components/contract/contract-header';
import { useEdlListQuery, type EdlEntry } from '@/lib/api/deliverables';
import { deviationStatusLabel } from '@/lib/contract-labels';

/**
 * /contract/deviations — C20-133 prototype-parity rewrite.
 *
 * Visual contract: `screens-modules-tabs.js → contractDeviations()`
 * (lines 646-671). Layout:
 *
 *   <ContractHeader meta="SOW + CO + EDL count" actions="Filter · New deviation">
 *   <KPI strip>   Open · In resolution · Closed this period
 *   <FilterBar>   severity · status · deliverable (by name)
 *   <ListCard>    deviation_reports — E06-F06 table card
 *
 * Wire-up matrix (per ticket §requirements):
 *   - List + filters → useDeviationsListQuery (T-0068)
 *   - "New deviation"→ data-create="deviation" (T-0117 modal)
 *   - Header meta    → useEdlListQuery + useChangeOrderQuery (best-effort —
 *                       Pending copy when no SOW seed available)
 *
 * Backend gaps surfaced as Pending (never faked):
 *   - "Closed this period" cycle window — backend doesn't expose a per-
 *     cycle bucket; the KPI tile shows `resolved + waived` cumulative
 *     count with a transparent caption.
 *   - AI-confidence aggregate on the filter button — backend doesn't
 *     surface a per-page average; the prototype's `confChip(0.83)` is
 *     omitted rather than invented.
 */
export const Route = createFileRoute('/engagements/$engagementId/contract/deviations')({
  component: ContractDeviationsTab,
});

interface DeviationFilters {
  severity: DeviationSeverity | 'all';
  status: DeviationStatus | 'all';
  deliverableEntryId: string;
}

function ContractDeviationsTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/contract/deviations' });

  const [draft, setDraft] = useState<DeviationFilters>({
    severity: 'all',
    status: 'all',
    deliverableEntryId: '',
  });
  const [applied, setApplied] = useState<DeviationFilters>(draft);
  const [creating, setCreating] = useState(false);
  const [transitioning, setTransitioning] = useState<DeviationSummary | null>(null);

  const list = useDeviationsListQuery(engagementId, {
    ...(applied.severity !== 'all' ? { severity: applied.severity } : {}),
    ...(applied.status !== 'all' ? { status: applied.status } : {}),
    ...(applied.deliverableEntryId.trim()
      ? { deliverable_entry_id: applied.deliverableEntryId.trim() }
      : {}),
  });

  // For the header meta line we want a real "SOW v2 · CO count · EDL count"
  // string. The EDL list gives us the deliverable count cheaply; we omit
  // the SOW/CO bits when no SOW seed exists in the URL.
  const edl = useEdlListQuery(engagementId);

  const items = list.data?.items ?? [];
  const counts = useMemo(() => bucketByStatus(items), [items]);

  const onApply = () => setApplied(draft);
  const onClear = () => {
    const blank: DeviationFilters = { severity: 'all', status: 'all', deliverableEntryId: '' };
    setDraft(blank);
    setApplied(blank);
  };
  const isFiltered =
    applied.severity !== 'all' || applied.status !== 'all' || applied.deliverableEntryId !== '';

  return (
    <div className="space-y-6">
      <ContractHeader
        engagementId={engagementId}
        active="deviations"
        meta={<HeaderMeta edlCount={edl.data?.entries.length ?? null} />}
        actions={
          <>
            <Button
              type="button"
              onClick={() => setCreating(true)}
              size="sm"
              className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              New deviation
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiCard
          label="Open"
          value={counts.open}
          caption={counts.open > 0 ? 'High + medium severity' : 'No open deviations'}
          tone={counts.open > 0 ? 'critical' : 'neutral'}
        />
        <KpiCard
          // One name per status (C2-44): the tile says what the chip says.
          label={deviationStatusLabel('in_resolution')}
          value={counts.inRes}
          caption="Plan accepted by IV&V"
          tone={counts.inRes > 0 ? 'at-risk' : 'neutral'}
        />
        <KpiCard
          label="Closed (cumulative)"
          value={counts.resolved + counts.waived}
          caption={`${counts.resolved} resolved · ${counts.waived} waived`}
          tone="healthy"
        />
      </div>

      <StatusInlineLegend counts={counts} />

      <FilterBar
        deliverables={edl.data?.entries ?? []}
        draft={draft}
        applied={applied}
        onChangeDraft={setDraft}
        onApply={onApply}
        onClear={onClear}
        isFiltered={isFiltered}
      />

      <EscalationBanner engagementId={engagementId} items={items} />

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ink-100 px-5 py-3">
            <div>
              <h2 className="text-h3 font-semibold text-ink-900">Deviation reports</h2>
              <p className="text-meta text-ink-500">
                Each row carries severity, SLA, source citation, and links to the underlying
                register entry.
              </p>
            </div>
            <span className="text-meta text-ink-500">
              {items.length} {items.length === 1 ? 'row' : 'rows'}
            </span>
          </div>
          <div className="px-4 py-3">
            {list.isLoading ? (
              <div className="flex items-center gap-2 text-body-sm text-ink-500">
                <Spinner /> Loading deviations…
              </div>
            ) : list.error ? (
              <ErrorMessage error={list.error} />
            ) : items.length === 0 ? (
              <EmptyState
                title={isFiltered ? 'No deviations match the current filters' : 'No deviations yet'}
                description={
                  isFiltered
                    ? 'Try clearing the filter or widening the severity / status criteria.'
                    : 'Nothing has deviated from the contract yet. Deviations are raised automatically when a deliverable is rejected, returned for revision, or passes its due date, and you can log one by hand with New deviation.'
                }
              />
            ) : (
              <ul className="flex flex-col gap-3" role="list">
                {items.map((d) => (
                  <li key={d.deviation_entry_id}>
                    <DeviationCard
                      engagementId={engagementId}
                      deviation={d}
                      onTransition={() => setTransitioning(d)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      <CreateDeviationModal
        engagementId={engagementId}
        open={creating}
        onOpenChange={setCreating}
      />
      {transitioning ? (
        <TransitionDeviationModal
          engagementId={engagementId}
          deviation={transitioning}
          open={transitioning !== null}
          onOpenChange={(next) => {
            if (!next) setTransitioning(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ===========================================================================
// Header meta — best-effort SOW + CO + EDL summary; falls back to Pending.
// ===========================================================================

function HeaderMeta({ edlCount }: { edlCount: number | null }) {
  // We don't have an engagement-scoped SOW list endpoint (gap flagged in
  // sow.ts). When no SOW seed is in the URL, render the EDL count alone
  // rather than inventing SOW / CO numbers.
  const edlText = edlCount == null ? 'EDL pending' : `${edlCount} deliverables tracked`;
  return <span>{edlText}</span>;
}

interface StatusCounts {
  open: number;
  inRes: number;
  resolved: number;
  waived: number;
}

function bucketByStatus(items: readonly DeviationSummary[]): StatusCounts {
  let open = 0;
  let inRes = 0;
  let resolved = 0;
  let waived = 0;
  for (const d of items) {
    if (d.status === 'open') open += 1;
    else if (d.status === 'in_resolution') inRes += 1;
    else if (d.status === 'resolved') resolved += 1;
    else if (d.status === 'waived') waived += 1;
  }
  return { open, inRes, resolved, waived };
}

function StatusInlineLegend({ counts }: { counts: StatusCounts }) {
  if (counts.open + counts.inRes + counts.resolved + counts.waived === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-meta text-ink-500">
      <span>Status:</span>
      {counts.open > 0 ? <PostureChip status="critical">{counts.open} open</PostureChip> : null}
      {counts.inRes > 0 ? (
        <PostureChip status="at-risk">{counts.inRes} in resolution</PostureChip>
      ) : null}
      {counts.resolved > 0 ? (
        <PostureChip status="healthy">{counts.resolved} resolved</PostureChip>
      ) : null}
      {counts.waived > 0 ? (
        <PostureChip status="unassessed">{counts.waived} waived</PostureChip>
      ) : null}
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: number | string;
  caption: string;
  tone?: 'neutral' | 'healthy' | 'at-risk' | 'critical';
}

function KpiCard({ label, value, caption, tone = 'neutral' }: KpiCardProps) {
  const accent =
    tone === 'critical'
      ? 'border-l-posture-critical'
      : tone === 'at-risk'
        ? 'border-l-accent-amber-600'
        : tone === 'healthy'
          ? 'border-l-posture-healthy'
          : 'border-l-ink-200';
  const valueClass =
    tone === 'critical'
      ? 'text-posture-critical'
      : tone === 'at-risk'
        ? 'text-accent-amber-700'
        : 'text-ink-900';
  return (
    <Card className={`overflow-hidden border-l-[3px] ${accent}`}>
      <CardContent className="p-4">
        <div className="text-meta uppercase tracking-wide text-ink-500">{label}</div>
        <div className={`mt-1 text-h2 font-semibold tabular-nums ${valueClass}`}>{value}</div>
        <div className="mt-1 text-meta text-ink-500">{caption}</div>
      </CardContent>
    </Card>
  );
}

function FilterBar({
  deliverables,
  draft,
  applied,
  onChangeDraft,
  onApply,
  onClear,
  isFiltered,
}: {
  /** The EDL, so the deliverable filter offers names rather than ids (C2-31). */
  deliverables: ReadonlyArray<EdlEntry>;
  draft: DeviationFilters;
  applied: DeviationFilters;
  onChangeDraft: (next: DeviationFilters) => void;
  onApply: () => void;
  onClear: () => void;
  isFiltered: boolean;
}) {
  const dirty =
    draft.severity !== applied.severity ||
    draft.status !== applied.status ||
    draft.deliverableEntryId !== applied.deliverableEntryId;
  return (
    <form
      role="search"
      aria-label="Filter deviations"
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
      className="flex flex-wrap items-end gap-3 rounded-card border border-ink-100 bg-paper-50 p-3"
    >
      <div className="flex items-center gap-2 text-meta text-ink-700">
        <Filter className="h-4 w-4" aria-hidden="true" />
        <span className="font-medium">Filter</span>
      </div>
      <label className="flex flex-col gap-1 text-meta text-ink-500">
        <span className="uppercase tracking-wide">Severity</span>
        <select
          value={draft.severity}
          onChange={(e) =>
            onChangeDraft({ ...draft, severity: e.target.value as DeviationFilters['severity'] })
          }
          className="h-9 rounded-md border border-ink-100 bg-paper-0 px-2 text-body-sm text-ink-900"
        >
          <option value="all">All severities</option>
          {DEVIATION_SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-meta text-ink-500">
        <span className="uppercase tracking-wide">Status</span>
        <select
          value={draft.status}
          onChange={(e) =>
            onChangeDraft({ ...draft, status: e.target.value as DeviationFilters['status'] })
          }
          className="h-9 rounded-md border border-ink-100 bg-paper-0 px-2 text-body-sm text-ink-900"
        >
          <option value="all">All statuses</option>
          {DEVIATION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {deviationStatusLabel(s)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-meta text-ink-500">
        <span className="uppercase tracking-wide">Deliverable</span>
        <select
          value={draft.deliverableEntryId}
          onChange={(e) => onChangeDraft({ ...draft, deliverableEntryId: e.target.value })}
          className="h-9 w-64 rounded-md border border-ink-100 bg-paper-0 px-2 text-body-sm text-ink-900"
        >
          <option value="">All deliverables</option>
          {deliverables.map((d) => (
            <option key={d.register_entry_id} value={d.register_entry_id}>
              {d.sow_ref ? `${d.sow_ref} · ${d.title}` : d.title}
            </option>
          ))}
        </select>
      </label>
      <div className="ml-auto flex items-end gap-2">
        {isFiltered ? (
          <Button type="button" variant="outline" size="sm" onClick={onClear}>
            Clear
          </Button>
        ) : null}
        <Button type="submit" size="sm" disabled={!dirty}>
          Apply
        </Button>
      </div>
    </form>
  );
}

export { ContractDeviationsTab };
